import { useEffect, useState, useMemo, useCallback } from "react";
import Layout from "../components/Layout";
import TaskCard from "../components/TaskCard";
import TaskFormDialog from "../components/TaskFormDialog";
import ProjectShareBar from "../components/ProjectShareBar";
import ProjectDeliverables from "../components/ProjectDeliverables";
import SearchBar, { matchesSearch } from "../components/SearchBar";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, ListChecks, Hourglass, CheckCircle, Circle, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const COLUMNS = [
  { key: "todo", label: "To Do", icon: Circle, accent: "border-t-zinc-400" },
  { key: "in_progress", label: "Working On It", icon: Hourglass, accent: "border-t-blue-600" },
  { key: "complete", label: "Complete", icon: CheckCircle, accent: "border-t-emerald-600" },
];

const INITIAL_STATS = { total: 0, todo: 0, in_progress: 0, complete: 0 };
const PAGE_SIZE = 30;
const FIRST_PAGES = { todo: 0, in_progress: 0, complete: 0 };

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectMeta, setProjectMeta] = useState([]);
  const [stats, setStats] = useState(INITIAL_STATS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [pages, setPages] = useState(FIRST_PAGES);
  const [pendingDeleteTask, setPendingDeleteTask] = useState(null);
  const [deletingTask, setDeletingTask] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [t, u, s, p] = await Promise.all([
        http.get("/tasks"),
        http.get("/users").catch(() => ({ data: [] })),
        http.get("/stats"),
        http.get("/projects").catch(() => ({ data: [] })),  // members can't fetch — falls back to []
      ]);
      setTasks(t.data);
      setUsers(u.data);
      setStats(s.data);
      setProjectMeta(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Auto-refresh the Studio Board every 30 s so newly assigned / updated tasks appear
  // without needing a manual reload. Also refreshes on window focus.
  useEffect(() => {
    const t = setInterval(() => { loadAll(); }, 30_000);
    const onFocus = () => loadAll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [loadAll]);

  const changeStatus = useCallback(async (id, status) => {
    try {
      await http.patch(`/tasks/${id}/status`, { status });
      toast.success(status === "complete" ? "Task completed" : "Status updated");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [loadAll]);

  const deleteTask = useCallback((task) => {
    // task can be an id (legacy) or the whole task object — normalize
    const t = typeof task === "string" ? { id: task, title: "this task" } : task;
    setPendingDeleteTask(t);
  }, []);

  const confirmDeleteTask = useCallback(async () => {
    if (!pendingDeleteTask) return;
    setDeletingTask(true);
    try {
      await http.delete(`/tasks/${pendingDeleteTask.id}`);
      toast.success("Task deleted");
      setPendingDeleteTask(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingTask(false);
    }
  }, [pendingDeleteTask, loadAll]);

  const openEdit = useCallback((task) => { setEditing(task); setDialogOpen(true); }, []);
  const openCreate = useCallback(() => { setEditing(null); setDialogOpen(true); }, []);

  const metaByProject = useMemo(() => {
    const m = {};
    (projectMeta || []).forEach((p) => { m[p.name] = p; });
    return m;
  }, [projectMeta]);

  const visibleTasks = useMemo(() => {
    return tasks.filter((t) => {
      const meta = metaByProject[t.project] || {};
      return matchesSearch(search, t.project, meta.client_name, meta.client_contact, meta.client_email, t.title);
    });
  }, [tasks, search, metaByProject]);

  // When the search narrows the board to exactly one project, expose its share bar + deliverables.
  const activeProject = useMemo(() => {
    if (!search.trim()) return null;
    const names = new Set(visibleTasks.map((t) => t.project).filter(Boolean));
    return names.size === 1 ? [...names][0] : null;
  }, [search, visibleTasks]);

  useEffect(() => { setPages(FIRST_PAGES); }, [search]);

  // Priority order: high > medium > low. Overdue/urgent tasks bubble to the top
  // regardless of priority so the red-bar work is impossible to miss.
  const PRIO_RANK = { high: 0, medium: 1, low: 2 };
  const URGENCY_RANK = { overdue: 0, urgent: 1, ok: 2, none: 2 };
  const MS_48H = 48 * 60 * 60 * 1000;

  const urgencyState = (t) => {
    if (!t.due_date || t.status === "complete") return "none";
    const d = new Date(t.due_date);
    if (isNaN(d)) return "none";
    d.setHours(23, 59, 59, 999);
    const diff = d.getTime() - Date.now();
    if (diff < 0) return "overdue";
    if (diff <= MS_48H) return "urgent";
    return "ok";
  };

  const sortForColumn = (list, columnKey) => {
    // For To Do + Working: priority first, then earliest due date.
    // For Complete: latest updated first.
    if (columnKey === "complete") {
      return [...list].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    }
    return [...list].sort((a, b) => {
      const uA = URGENCY_RANK[urgencyState(a)] ?? 2;
      const uB = URGENCY_RANK[urgencyState(b)] ?? 2;
      if (uA !== uB) return uA - uB;
      const pA = PRIO_RANK[a.priority] ?? 3;
      const pB = PRIO_RANK[b.priority] ?? 3;
      if (pA !== pB) return pA - pB;
      const dA = a.due_date || "9999-12-31";
      const dB = b.due_date || "9999-12-31";
      if (dA !== dB) return dA.localeCompare(dB);
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
  };

  const grouped = { todo: [], in_progress: [], complete: [] };
  visibleTasks.forEach((t) => grouped[t.status]?.push(t));
  Object.keys(grouped).forEach((k) => { grouped[k] = sortForColumn(grouped[k], k); });

  return (
    <Layout
      title={isManagerOrAdmin ? "Studio Board" : "My Assignments"}
      subtitle={isManagerOrAdmin
        ? "Assign work, monitor progress, and keep every project moving from draft to delivery."
        : "Everything currently on your plate. Move cards through the phases as you go."}
      actions={
        isManagerOrAdmin && (
          <Button
            data-testid="new-task-button"
            onClick={openCreate}
            className="rounded-none h-11 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-5 shadow-brutal-sm"
          >
            <Plus size={14} weight="bold" className="mr-2" /> New Task
          </Button>
        )
      }
    >
      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-300 bg-white mb-8" data-testid="stats-strip">
        <Stat label="Total" value={stats.total} icon={ListChecks} />
        <Stat label="To Do" value={stats.todo} icon={Circle} />
        <Stat label="Working On" value={stats.in_progress} icon={Hourglass} accent="text-blue-600" />
        <Stat label="Completed" value={stats.complete} icon={CheckCircle} accent="text-emerald-600" />
      </div>

      {/* Search */}
      <div className="mb-4" data-testid="board-search">
        <SearchBar
          value={search}
          onChange={setSearch}
          testId="board-search-input"
          placeholder="Search by Job No., Client Name or Phone No.…"
        />
      </div>

      {activeProject && (
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="active-project-bar">
          <span className="label-overline text-zinc-500">Showing project</span>
          <span className="label-overline bg-zinc-900 text-white px-3 py-1.5" data-testid="active-project-name">
            {activeProject}{metaByProject[activeProject]?.client_name ? ` · ${metaByProject[activeProject].client_name}` : ""}
          </span>
          {isManagerOrAdmin && (
            <div className="ml-auto">
              <ProjectShareBar projectName={activeProject} onRegenerated={loadAll} />
            </div>
          )}
        </div>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-0 border border-zinc-300 bg-white" data-testid="kanban-grid">
        {COLUMNS.map((col, idx) => {
          const Icon = col.icon;
          const list = grouped[col.key];
          const pageCount = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
          const page = Math.min(pages[col.key], pageCount - 1);
          const pageItems = list.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
          const setPage = (n) => setPages((p) => ({ ...p, [col.key]: Math.max(0, Math.min(pageCount - 1, n)) }));
          return (
            <div
              key={col.key}
              data-testid={`column-${col.key}`}
              className={`bg-zinc-50 border-t-4 ${col.accent} ${
                idx < COLUMNS.length - 1 ? "md:border-r xl:border-r border-b md:border-b-0 border-zinc-300" : ""
              }`}
            >
              <div className="px-4 sm:px-5 py-4 border-b border-zinc-300 bg-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={16} weight="bold" className="text-zinc-700" />
                  <span className="label-overline text-zinc-900">{col.label}</span>
                </div>
                <span className="label-overline bg-zinc-900 text-white px-2 py-0.5">{list.length}</span>
              </div>
              <div className="p-3 sm:p-4 space-y-3 min-h-[320px] md:min-h-[400px]">
                <AnimatePresence mode="popLayout">
                  {list.length === 0 ? (
                    <EmptyState label={col.label} />
                  ) : (
                    pageItems.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        isAdmin={isManagerOrAdmin}
                        me={user}
                        onChangeStatus={changeStatus}
                        onDelete={deleteTask}
                        onEdit={openEdit}
                        onRefresh={loadAll}
                      />
                    ))
                  )}
                </AnimatePresence>
              </div>
              {pageCount > 1 && (
                <ColumnPager col={col.key} page={page} pageCount={pageCount} total={list.length} onChange={setPage} />
              )}
            </div>
          );
        })}
      </div>

      {isManagerOrAdmin && (
        <TaskFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          users={users}
          editing={editing ? tasks.find((t) => t.id === editing.id) || editing : null}
          onSaved={loadAll}
        />
      )}

      {activeProject && (
        <div className="mt-8">
          <ProjectDeliverables
            projectName={activeProject}
            canUpload={user?.role === "admin" || user?.role === "manager" || user?.role === "member"}
            canDelete={isManagerOrAdmin}
          />
        </div>
      )}

      <AlertDialog open={!!pendingDeleteTask} onOpenChange={(v) => !v && setPendingDeleteTask(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Delete task?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will permanently delete <strong>&quot;{pendingDeleteTask?.title || "this task"}&quot;</strong>
              {pendingDeleteTask?.references?.length > 0 && (
                <> along with <strong>{pendingDeleteTask.references.length} reference file(s)</strong></>
              )}
              . This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-task"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-task"
              disabled={deletingTask}
              onClick={confirmDeleteTask}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs"
            >
              {deletingTask ? "Deleting…" : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function Stat({ label, value, icon: Icon, accent = "text-zinc-900" }) {
  return (
    <div className="border-r border-zinc-300 last:border-r-0 p-5 flex items-center justify-between">
      <div>
        <div className="label-overline text-zinc-500">{label}</div>
        <div className={`font-display text-3xl font-black tracking-tighter mt-1 ${accent}`}>{value}</div>
      </div>
      <Icon size={22} weight="bold" className={accent} />
    </div>
  );
}

function ColumnPager({ col, page, pageCount, total, onChange }) {
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  return (
    <div className="px-4 py-2.5 border-t border-zinc-300 bg-white flex items-center justify-between" data-testid={`pager-${col}`}>
      <span className="font-mono text-[11px] text-zinc-500" data-testid={`pager-${col}-range`}>{from}–{to} of {total}</span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onChange(page - 1)} disabled={page === 0} data-testid={`pager-${col}-prev`}
                className="p-1.5 border border-zinc-300 hover:border-zinc-900 disabled:opacity-30 disabled:hover:border-zinc-300">
          <CaretLeft size={12} weight="bold" />
        </button>
        <span className="label-overline px-2">{page + 1} / {pageCount}</span>
        <button type="button" onClick={() => onChange(page + 1)} disabled={page >= pageCount - 1} data-testid={`pager-${col}-next`}
                className="p-1.5 border border-zinc-300 hover:border-zinc-900 disabled:opacity-30 disabled:hover:border-zinc-300">
          <CaretRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-zinc-300 py-10 text-center">
      <div className="label-overline text-zinc-400">No {label} tasks</div>
    </div>
  );
}
